import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Note, Folder } from '../types';

// Collection names
const COLLECTIONS = {
  NOTES: 'notes',
  FOLDERS: 'folders',
  HISTORY: 'history'
} as const;

// Helper to convert Firestore timestamp to Date
const timestampToDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp?.toDate) {
    return timestamp.toDate();
  }
  if (timestamp?.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date(timestamp);
};

// Helper to convert Note from Firestore format
const convertFirestoreNote = (doc: any): Note => {
  const data = doc.data();
  return {
    ...data,
    id: doc.id,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
    checklistItems: data.checklistItems?.map((item: any) => ({
      ...item,
      createdAt: timestampToDate(item.createdAt)
    })) || []
  };
};

// Helper to convert Folder from Firestore format
const convertFirestoreFolder = (doc: any): Folder => {
  const data = doc.data();
  return {
    ...data,
    id: doc.id,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt)
  };
};

// Helper to remove undefined values from an object (Firestore doesn't accept undefined)
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) return null;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object' && !(obj instanceof Date) && !(obj instanceof Timestamp)) {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = removeUndefined(value);
      }
    }
    return cleaned;
  }
  return obj;
};

export const firestoreService = {
  // Notes
  async getAllNotes(userId?: string): Promise<Note[]> {
    try {
      const notesRef = collection(db, COLLECTIONS.NOTES);
      let q;
      
      // If userId is provided, filter by user, otherwise get all notes
      if (userId) {
        q = query(notesRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
      } else {
        q = query(notesRef, orderBy('updatedAt', 'desc'));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(convertFirestoreNote);
    } catch (error) {
      console.error('Error getting notes:', error);
      return [];
    }
  },

  async getNote(id: string): Promise<Note | null> {
    try {
      const docRef = doc(db, COLLECTIONS.NOTES, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return convertFirestoreNote(docSnap);
      }
      return null;
    } catch (error) {
      console.error('Error getting note:', error);
      return null;
    }
  },

  async addNote(note: Note, userId?: string): Promise<string> {
    try {
      const noteData = removeUndefined({
        ...note,
        userId: userId || 'anonymous',
        // Convert undefined folderId to null for Firestore
        folderId: note.folderId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        checklistItems: note.checklistItems?.map(item => ({
          ...item,
          createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : serverTimestamp()
        })) || []
      });
      
      // Use setDoc with the note's existing ID to preserve it
      const docRef = doc(db, COLLECTIONS.NOTES, note.id);
      await setDoc(docRef, noteData);
      return note.id;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  async updateNote(id: string, updates: Partial<Note>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.NOTES, id);
      let updateData: any = {
        ...updates,
        // Convert undefined folderId to null for Firestore
        ...(updates.folderId !== undefined && { folderId: updates.folderId || null }),
        updatedAt: serverTimestamp()
      };
      
      // Handle checklist items timestamps
      if (updates.checklistItems) {
        updateData.checklistItems = updates.checklistItems.map(item => ({
          ...item,
          createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : item.createdAt
        }));
      }
      
      // Remove undefined values
      updateData = removeUndefined(updateData);
      
      // Use setDoc with merge to handle cases where document might not exist yet
      await setDoc(docRef, updateData, { merge: true });
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async deleteNote(id: string): Promise<void> {
    try {
      await deleteDoc(doc(db, COLLECTIONS.NOTES, id));
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  // Folders
  async getAllFolders(userId?: string): Promise<Folder[]> {
    try {
      const foldersRef = collection(db, COLLECTIONS.FOLDERS);
      let q;
      
      // If userId is provided, filter by user, otherwise get all folders
      if (userId) {
        q = query(foldersRef, where('userId', '==', userId), orderBy('name'));
      } else {
        q = query(foldersRef, orderBy('name'));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(convertFirestoreFolder);
    } catch (error) {
      console.error('Error getting folders:', error);
      return [];
    }
  },

  async addFolder(folder: Omit<Folder, 'id'>, userId?: string): Promise<string> {
    try {
      const folderData = {
        ...folder,
        userId: userId || 'anonymous',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.FOLDERS), folderData);
      return docRef.id;
    } catch (error) {
      console.error('Error adding folder:', error);
      throw error;
    }
  },

  async updateFolder(id: string, updates: Partial<Folder>): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.FOLDERS, id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating folder:', error);
      throw error;
    }
  },

  async deleteFolder(id: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      
      // Delete the folder
      batch.delete(doc(db, COLLECTIONS.FOLDERS, id));
      
      // Update notes in this folder to have no folder
      const notesRef = collection(db, COLLECTIONS.NOTES);
      const notesQuery = query(notesRef, where('folderId', '==', id));
      const notesSnapshot = await getDocs(notesQuery);
      
      notesSnapshot.docs.forEach((noteDoc) => {
        batch.update(noteDoc.ref, { 
          folderId: null,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error deleting folder:', error);
      throw error;
    }
  },

  // Real-time listeners
  subscribeToNotes(userId: string | undefined, callback: (notes: Note[]) => void) {
    const notesRef = collection(db, COLLECTIONS.NOTES);
    let q = query(notesRef, orderBy('updatedAt', 'desc'));
    
    if (userId) {
      q = query(notesRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
    }
    
    return onSnapshot(q, (snapshot) => {
      const notes = snapshot.docs.map(convertFirestoreNote);
      callback(notes);
    }, (error) => {
      console.error('Error in notes subscription:', error);
    });
  },

  subscribeToFolders(userId: string | undefined, callback: (folders: Folder[]) => void) {
    const foldersRef = collection(db, COLLECTIONS.FOLDERS);
    let q = query(foldersRef, orderBy('name'));
    
    if (userId) {
      q = query(foldersRef, where('userId', '==', userId), orderBy('name'));
    }
    
    return onSnapshot(q, (snapshot) => {
      const folders = snapshot.docs.map(convertFirestoreFolder);
      callback(folders);
    }, (error) => {
      console.error('Error in folders subscription:', error);
    });
  },

  // Batch operations for sync
  async batchImport(notes: Note[], folders: Folder[], userId?: string) {
    try {
      const batch = writeBatch(db);
      
      // Add folders
      folders.forEach((folder) => {
        const folderRef = doc(collection(db, COLLECTIONS.FOLDERS));
        batch.set(folderRef, {
          ...folder,
          userId: userId || 'anonymous',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      // Add notes
      notes.forEach((note) => {
        const noteRef = doc(collection(db, COLLECTIONS.NOTES));
        batch.set(noteRef, {
          ...note,
          userId: userId || 'anonymous',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error in batch import:', error);
      throw error;
    }
  }
};